// Supported PATIENT languages for the Live Translation feature.
//
// English is intentionally NOT in this list — the nurse always speaks
// English, so it's the implicit "other side" of every translation. Picking
// English as the target would mean translating English↔English, which the
// model would happily do (passthrough/conversational) and which is never
// what a clinical session needs.
//
// `code` is the ISO 639-1 code injected into the bidirectional prompt's
// `<language>` placeholder. `label` is the human-facing string shown in the
// mobile picker.
//
// Mandarin and Cantonese are SEPARATE entries, not one "Chinese". They are not
// mutually intelligible, and a single "Chinese" option meant a Cantonese
// patient got a Mandarin session they could not understand (field report,
// 2026-08-03). `yue` is ISO 639-3 — Cantonese has no 639-1 code — and it is
// only ever used as a prompt token + a stored label here, so the wider code
// carries it fine. `zh` stays Mandarin so existing session rows keep meaning
// what they meant when they were written.
export const LIVE_TRANSLATION_LANGUAGES = [
    { code: "es", label: "Spanish" },
    { code: "ru", label: "Russian" },
    { code: "zh", label: "Mandarin" },
    { code: "yue", label: "Cantonese" },
    { code: "ht", label: "Haitian Creole" },
    { code: "bn", label: "Bengali" },
    { code: "pt", label: "Portuguese" },
    { code: "fr", label: "French" },
    { code: "de", label: "German" },
    { code: "ja", label: "Japanese" },
    { code: "ko", label: "Korean" },
    { code: "hi", label: "Hindi" },
    { code: "id", label: "Indonesian" },
    { code: "vi", label: "Vietnamese" },
    { code: "it", label: "Italian" }
];
const SUPPORTED_CODES = new Set(LIVE_TRANSLATION_LANGUAGES.map((l) => l.code));
export function isSupportedLanguage(code) {
    return SUPPORTED_CODES.has(code);
}
export const LIVE_TRANSLATION_PROVIDERS = [
    "openai",
    "gemini"
];
// gemini-3.5-live-translate-preview is a dedicated speech-to-speech translate
// model: one fixed output language per session, input language auto-detected.
// We pin the preview id in one place so a GA rename is a single edit.
export const GEMINI_LIVE_TRANSLATE_MODEL = "gemini-3.5-live-translate-preview";
// Interpreter mode: a REGULAR Live model driven by a bidirectional prompt does
// both directions in ONE session (the dedicated translate model can't — it
// outputs one fixed language per session and echoes same-language input). This
// is the path we ship. Half-cascade is lower-latency; the native-audio model
// (gemini-2.5-flash-native-audio-latest) has nicer voice but more lag.
export const GEMINI_LIVE_INTERPRETER_MODEL = "gemini-3.1-flash-live-preview";
// Pinned output voice (prebuilt). Each model otherwise defaults differently.
export const GEMINI_LIVE_VOICE_NAME = "Kore";
// VAD / turn-taking: how long a pause ends the speaker's turn (lower = snappier
// but risks cutting in mid-sentence) + a little pre-roll.
export const GEMINI_LIVE_VAD_SILENCE_MS = 400;
export const GEMINI_LIVE_VAD_PREFIX_MS = 20;
// Live API audio is always raw little-endian 16-bit PCM. Input is natively
// 16 kHz; output is always 24 kHz. The mobile captures at INPUT_RATE and
// plays back at OUTPUT_RATE.
export const GEMINI_LIVE_AUDIO_INPUT_RATE = 16_000;
export const GEMINI_LIVE_AUDIO_OUTPUT_RATE = 24_000;
// Ephemeral-token WebSocket endpoint. Verified against the @google/genai SDK +
// a live setupComplete: ephemeral tokens use the v1alpha surface, the
// `BidiGenerateContentConstrained` method (NOT plain BidiGenerateContent), and
// the token is passed as the `access_token` query param appended RAW (no URL-
// encoding of the `auth_tokens/...` slash). Anything else → close code 1008
// "unregistered callers".
export const GEMINI_LIVE_WS_HOST = "generativelanguage.googleapis.com";
export const GEMINI_LIVE_WS_PATH = "/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContentConstrained";
/**
 * API-key WebSocket endpoint, for a connection opened by a SERVER.
 *
 * The constrained path above is the ephemeral-token surface and rejects an API
 * key outright:
 *
 *   close 1007 — "Missing or malformed auth token in request. Obtain one from
 *   CreateAuthToken and pass it in an `access_token` query parameter"
 *
 * A worker holding the socket has the key legitimately and has nothing to scope
 * down for a handset, so it wants the plain v1beta method with `?key=`. The two
 * are not interchangeable, and reaching for the wrong one fails as a socket that
 * closes before `setupComplete` rather than as an auth error you can read.
 */
export const GEMINI_LIVE_WS_PATH_API_KEY = "/ws/google.ai.generativelanguage.v1beta.GenerativeService.BidiGenerateContent";
// The nurse always speaks English; it is the implicit other side of every
// translation (see the note above on why English is absent from
// LIVE_TRANSLATION_LANGUAGES). The Gemini path needs it as an explicit
// targetLanguageCode for the patient→nurse session.
export const NURSE_LANGUAGE_CODE = "en";
export const LIVE_TRANSLATION_TURN_ROLES = [
    "patient",
    "nurse",
    "system"
];
export const LIVE_TRANSLATION_SESSION_STATUSES = [
    "active",
    "completed",
    "failed"
];
//# sourceMappingURL=liveTranslation.js.map